import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  listIncidents,
  createIncident,
  closeIncident,
  updateIncident,
  deleteIncident,
  type CreateIncidentDTO,
  type CloseIncidentDTO,
  type UpdateIncidentDTO,
} from '../api/incidents.api.ts';
import type { IncidentListParams } from '@/shared/types/incident.ts';

export const incidentKeys = {
  all: ['incidents'] as const,
  list: (params: IncidentListParams) => [...incidentKeys.all, 'list', params] as const,
  detail: (id: string) => [...incidentKeys.all, 'detail', id] as const,
};

export function useIncidentList(params: IncidentListParams, enabled = true) {
  return useQuery({
    queryKey: incidentKeys.list(params),
    queryFn: () => listIncidents(params),
    enabled,
  });
}

export function useCreateIncident(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIncidentDTO) => createIncident(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.all });
      toast.success('Incidente criado com sucesso');
      onSuccess?.();
    },
    onError: () => {
      toast.error('Erro ao criar incidente');
    },
  });
}

export function useCloseIncident(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CloseIncidentDTO }) =>
      closeIncident(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.all });
      toast.success('Incidente encerrado com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        const msg = err.response?.data?.error;
        if (typeof msg === 'string' && msg.includes('Causa raiz')) {
          toast.error('Causa raiz obrigatória para incidentes CRITICA');
        } else {
          toast.error(typeof msg === 'string' ? msg : 'Incidente já encerrado');
        }
      } else {
        toast.error('Erro ao encerrar incidente');
      }
    },
  });
}

export function useUpdateIncident(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateIncidentDTO }) =>
      updateIncident(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.all });
      toast.success('Incidente atualizado com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error(err.response?.data?.error ?? 'Incidente já encerrado');
      } else {
        toast.error('Erro ao atualizar incidente');
      }
    },
  });
}

export function useDeleteIncident(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIncident(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.all });
      toast.success('Incidente excluído com sucesso');
      onSuccess?.();
    },
    onError: () => {
      toast.error('Erro ao excluir incidente');
    },
  });
}

function isAxiosError(
  err: unknown,
): err is { response?: { status: number; data?: { error?: string } } } {
  return typeof err === 'object' && err !== null && 'response' in err;
}
